import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, BellRing, Info, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useTenant } from "../../contexts/TenantContext";
import { ensureWebPushSubscription, getWebPushState } from "../../lib/webPush";
import {
    EVENTS as FALLBACK_EVENTS, MODE_DISABLED, MODE_FULL_SCREEN, MODE_PUSH, resolveRule,
} from "../../lib/notificationRules";

const MODE_LABELS = {
    [MODE_PUSH]: "Push",
    [MODE_FULL_SCREEN]: "Full screen",
    [MODE_DISABLED]: "Off",
};
const MODE_HELP = {
    [MODE_PUSH]: "A normal notification. No alert dialog, no repeating sound.",
    [MODE_FULL_SCREEN]: "The notification plus an alert dialog that stays until someone acknowledges it.",
    [MODE_DISABLED]: "Nobody is notified about this event, on any device.",
};

/**
 * Says whether a browser on this machine can actually be notified, and exactly what is missing when it can't.
 *
 * Without this the console degrades in silence: the platform has no Web Push keys, or this browser declined, and
 * the admin simply never sees a notification — indistinguishable from the bug we just fixed. Each state names the
 * person who can fix it, because a tenant admin cannot add platform VAPID keys themselves.
 */
function DeliveryStatus({ webPush, onEnable }) {
    const { platform, browser } = webPush;
    if (platform === null || browser === null) return null;          // still loading
    if (browser === "unsupported") {
        return (
            <Note testid="notif-web-unsupported">
                This browser can&apos;t show notifications. The console still alerts you while this tab is open.
            </Note>
        );
    }
    if (!platform) {
        return (
            <Note testid="notif-web-unconfigured" warn>
                <b>Browser notifications are off for the whole platform.</b> Nobody on the web console will be
                notified while their browser is minimised — only while a console tab is open. Your Super Admin needs
                to add Web Push keys under <b>Super Admin → Firebase → Web Push</b> (generate them with{" "}
                <code className="bg-background/60 px-1 rounded">npx web-push generate-vapid-keys</code> and paste the
                public key, private key and a contact email). Phone apps are unaffected.
            </Note>
        );
    }
    if (browser === "denied") {
        return (
            <Note testid="notif-web-denied" warn>
                <b>This browser is blocking notifications.</b> Allow them for this site in your browser&apos;s site
                settings (the padlock next to the address bar → Notifications → Allow), then reload.
            </Note>
        );
    }
    if (browser !== "granted") {
        return (
            <Note testid="notif-web-prompt" warn>
                <b>This browser isn&apos;t receiving notifications yet.</b> Turn them on so you are alerted when the
                window is minimised.
                <button type="button" onClick={onEnable} data-testid="notif-web-enable"
                    className="touch-target ml-2 rounded-lg bg-amber-500 text-white text-xs font-bold px-3 py-1.5">
                    Enable
                </button>
            </Note>
        );
    }
    return (
        <Note testid="notif-web-ok">
            Browser notifications are on for this device — you&apos;ll be alerted even with the window minimised.
        </Note>
    );
}

function Note({ children, warn = false, testid }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-xl border px-3 py-2.5 flex gap-2.5 text-xs ${
                warn ? "border-amber-300 bg-amber-50 text-amber-900" : "border-border bg-muted/40 text-muted-foreground"
            }`}
        >
            {warn ? <AlertTriangle size={15} className="shrink-0 mt-0.5" /> : <Info size={15} className="shrink-0 mt-0.5" />}
            <p className="leading-snug">{children}</p>
        </div>
    );
}

/**
 * Setup → Notifications. Per-event notification behaviour for this tenant.
 *
 * Saved to ``tenant.notification_rules`` and resolved by backend/notification_policy.py, which every sender and
 * every client goes through. An event left on "Default" is stored as nothing at all and keeps whatever behaviour
 * it has today, so opening this page and saving without touching a row changes nothing.
 */
export default function AdminNotifications() {
    const { tenant, refresh } = useTenant() || {};
    const [events, setEvents] = useState(FALLBACK_EVENTS);
    const [rules, setRules] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [legacy, setLegacy] = useState({ configured: false, sound: false });
    // Delivery health for THIS browser, so "I set it to full screen and nothing happened" is answerable here.
    const [webPush, setWebPush] = useState({ platform: null, browser: null });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/notification-rules");
                setEvents(data?.events?.length ? data.events : FALLBACK_EVENTS);
                setRules(data?.rules || {});
                setLegacy({ configured: !!data?.legacy_alert_configured, sound: !!data?.alert_sound_configured });
                setWebPush({ platform: !!data?.web_push_configured, browser: (await getWebPushState()).state });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't load notification settings");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // What each row does right now, so "Default" shows the behaviour it inherits instead of an empty cell.
    const effective = useMemo(() => {
        const t = { ...(tenant || {}), notification_rules: rules };
        return Object.fromEntries(events.map((e) => [e.key, resolveRule(t, e.key)]));
    }, [events, rules, tenant]);

    const setRule = (key, patch) => setRules((r) => {
        const next = { ...r, [key]: { ...(r[key] || {}), ...patch } };
        // An empty rule means "default" — drop it rather than storing a half-written one.
        if (Object.keys(next[key]).length === 0) delete next[key];
        return next;
    });

    const resetRule = (key) => setRules((r) => {
        const next = { ...r };
        delete next[key];
        return next;
    });

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/notification-rules", { rules });
            setRules(data?.rules || {});
            await refresh?.();
            toast.success("Notification settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save notification settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="admin-notifications-loading">
                <Loader2 size={16} className="animate-spin" /> Loading notification settings…
            </div>
        );
    }

    return (
        <div className="p-3 sm:p-4 space-y-4" data-testid="admin-notifications">
            <header className="flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0">
                    <h1 className="bizil-type-h2 flex items-center gap-2"><BellRing size={20} /> Notifications</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        How each automatic notification behaves for this business. Events left on <b>Default</b> keep
                        their current behaviour.
                    </p>
                </div>
                <button
                    type="button" onClick={save} disabled={saving} data-testid="admin-notifications-save"
                    className="touch-target rounded-xl bg-primary text-primary-foreground font-bold px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
                </button>
            </header>

            <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5 flex gap-2.5 text-xs text-muted-foreground">
                <Info size={15} className="shrink-0 mt-0.5" />
                <div className="space-y-1">
                    <p>
                        <b>Full screen</b> alerts reach the shop, tenant admins and riders — never customers. Each
                        person can still switch them off for their own account from the sidebar.
                    </p>
                    <p>
                        {legacy.sound
                            ? "Alerts use your uploaded alert sound."
                            : "No alert sound is uploaded for this business, so alerts use the built-in tone. Ask your Super Admin to upload one."}
                    </p>
                </div>
            </div>

            <DeliveryStatus webPush={webPush} onEnable={async () => {
                const r = await ensureWebPushSubscription({ prompt: true });
                setWebPush((w) => ({ ...w, browser: r.state === "granted" ? "granted" : r.state }));
                if (r.state === "granted") toast.success("Browser notifications are on for this device.");
            }} />

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {events.map((e) => {
                    const rule = rules[e.key] || {};
                    const eff = effective[e.key] || {};
                    const isDefault = !rule.mode;
                    const modes = e.supports_full_screen
                        ? [MODE_PUSH, MODE_FULL_SCREEN, MODE_DISABLED]
                        : [MODE_PUSH, MODE_DISABLED];
                    return (
                        <div key={e.key} className="p-3 sm:p-4 border-b border-border last:border-0 space-y-3" data-testid={`notif-rule-${e.key}`}>
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="font-bold text-sm">{e.label}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{e.description}</p>
                                </div>
                                {!isDefault && (
                                    <button
                                        type="button" onClick={() => resetRule(e.key)}
                                        data-testid={`notif-rule-reset-${e.key}`}
                                        className="text-xs font-bold text-muted-foreground underline underline-offset-2 shrink-0"
                                    >
                                        Reset to default
                                    </button>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-1.5">
                                {modes.map((m) => {
                                    const on = rule.mode === m || (isDefault && eff.mode === m);
                                    return (
                                        <button
                                            key={m} type="button" title={MODE_HELP[m]}
                                            onClick={() => setRule(e.key, { mode: m })}
                                            data-testid={`notif-mode-${e.key}-${m}`}
                                            className={`touch-target rounded-lg px-3 py-2 text-xs font-bold border ${
                                                on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"
                                            } ${isDefault && on ? "opacity-70" : ""}`}
                                        >
                                            {MODE_LABELS[m]}{isDefault && on ? " (default)" : ""}
                                        </button>
                                    );
                                })}
                            </div>

                            {eff.mode !== MODE_DISABLED && (
                                <div className="flex flex-wrap gap-4 text-xs">
                                    <label className="inline-flex items-center gap-2 font-semibold">
                                        <input
                                            type="checkbox" className="w-4 h-4"
                                            data-testid={`notif-sound-${e.key}`}
                                            checked={eff.sound !== false}
                                            onChange={(ev) => setRule(e.key, { sound: ev.target.checked })}
                                        />
                                        Sound
                                    </label>
                                    <label className={`inline-flex items-center gap-2 font-semibold ${eff.mode === MODE_FULL_SCREEN ? "" : "opacity-40"}`}>
                                        <input
                                            type="checkbox" className="w-4 h-4"
                                            data-testid={`notif-repeat-${e.key}`}
                                            disabled={eff.mode !== MODE_FULL_SCREEN}
                                            checked={eff.mode === MODE_FULL_SCREEN && eff.repeat !== false}
                                            onChange={(ev) => setRule(e.key, { repeat: ev.target.checked })}
                                        />
                                        Repeat until acknowledged
                                    </label>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
                <p>
                    Phones show the notification whether the app is open, in the background or closed. The
                    full-screen alert dialog and its looping sound run while the app is open.
                </p>
                <p>
                    <b>Repeat until acknowledged</b> also re-sends the notification up to 3 more times, about 20
                    seconds apart, so a closed or pocketed phone keeps ringing. It stops the moment anyone opens
                    the alert or acts on the order. Android and iOS don&apos;t allow an app to take over a locked
                    screen or ring continuously, so this is the closest the platforms permit.
                </p>
            </div>
        </div>
    );
}
